import { Head, useForm } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CreditCard } from 'lucide-react'

type Account = {
  id: string
  name: string
  slug: string
  type: string
  created_at: string
}

type User = {
  id: string
  name: string
  email_address: string
  role: string
}

type Identity = {
  id: string
  email_address: string
}

type Props = {
  account: Account
  user: User
  identity: Identity
}

export default function SettingsIndex({ account, user, identity }: Props) {
  const profileForm = useForm({
    name: user.name,
    email: identity.email_address
  })

  const workspaceForm = useForm({
    name: account.name
  })

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    profileForm.patch(`/${account.slug}/profile`)
  }

  const handleWorkspaceSubmit = (e: React.FormEvent) => {
    e.preventDefault()
  }

  return (
    <DashboardLayout title="Settings">
      <Head title="Settings" />

      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold">Settings</h1>
          <p className="text-[13px] text-muted-foreground mt-1">
            Manage your account, workspace, and preferences
          </p>
        </div>

        {/* Profile Settings */}
        <div className="pb-8 border-b border-border/40">
          <div className="mb-4">
            <h2 className="text-base font-medium">Profile</h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Update your personal information and email preferences
            </p>
          </div>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="profile-name" className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Full Name</Label>
                <Input
                  id="profile-name"
                  value={profileForm.data.name}
                  onChange={e => profileForm.setData('name', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="profile-email" className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Email Address</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={profileForm.data.email}
                  onChange={e => profileForm.setData('email', e.target.value)}
                  disabled
                  className="h-8 text-sm bg-muted"
                />
                <p className="text-[12px] text-muted-foreground">
                  Email cannot be changed
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 pt-1">
              <Button
                type="submit"
                size="sm"
                className="h-8 text-[13px]"
                disabled={profileForm.processing}
              >
                Save Changes
              </Button>
              {profileForm.recentlySuccessful && (
                <p className="text-[13px] text-primary">Profile updated!</p>
              )}
            </div>
          </form>
        </div>

        {/* Workspace Settings */}
        <div className="pb-8 border-b border-border/40">
          <div className="mb-4">
            <h2 className="text-base font-medium">Workspace</h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Manage your workspace name and URL
            </p>
          </div>
          <form onSubmit={handleWorkspaceSubmit} className="space-y-4 max-w-md">
            <div className="space-y-1.5">
              <Label htmlFor="workspace-name" className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Workspace Name</Label>
              <Input
                id="workspace-name"
                value={workspaceForm.data.name}
                onChange={e => workspaceForm.setData('name', e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Workspace URL</Label>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md text-[13px]">
                <span className="text-muted-foreground">launch.sidebrief.com/</span>
                <span className="font-medium">{account.slug}</span>
              </div>
              <p className="text-[12px] text-muted-foreground">
                This is your workspace's unique identifier
              </p>
            </div>
            <div className="flex items-center gap-4 pt-1">
              <Button
                type="submit"
                size="sm"
                className="h-8 text-[13px]"
                disabled={workspaceForm.processing}
              >
                Update Workspace
              </Button>
              <p className="text-[12px] text-muted-foreground">
                Created {new Date(account.created_at).toLocaleDateString()}
              </p>
            </div>
          </form>
        </div>

        {/* Security Section */}
        <div className="pb-8 border-b border-border/40">
          <div className="mb-4">
            <h2 className="text-base font-medium">Security</h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Authentication is handled via magic link — no password needed
            </p>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-[13px] font-medium">Two-Factor Authentication</p>
                <p className="text-[12px] text-muted-foreground">
                  Add an extra layer of security
                </p>
              </div>
              <span className="text-[12px] text-muted-foreground">Coming Soon</span>
            </div>
          </div>
        </div>

        {/* Billing - Coming Soon */}
        <div>
          <div className="mb-4">
            <h2 className="text-base font-medium">Billing</h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Manage your payment methods and view billing history
            </p>
          </div>
          <div className="flex flex-col items-center text-center py-10">
            <CreditCard className="w-10 h-10 text-muted-foreground/40 mb-3" />
            <p className="text-[13px] font-medium mb-1">Billing information</p>
            <p className="text-[12px] text-muted-foreground mb-3">
              View your invoices and manage payment methods
            </p>
            <span className="text-[12px] text-muted-foreground">Coming Soon</span>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
